/**
 * ProductsTab — Mini-orchestrator for the "Catalogue Produits" main tab.
 *
 * Contains 2 sub-sections rendered via a pill-style sub-nav:
 *   0 → Catégories de Produit  (/admin/product-categories)
 *   1 → Produits               (/admin/products)
 *
 * API endpoints used:
 *   GET  /admin/product-categories     (brand-scoped via X-Brand)
 *   POST /admin/product-categories
 *   PATCH /admin/product-categories/:id
 *   DELETE /admin/product-categories/:id
 *
 *   GET  /admin/products               (brand-scoped via X-Brand)
 *   POST /admin/products
 *   PATCH /admin/products/:id
 *   DELETE /admin/products/:id
 *
 * Backend model reference (Product):
 *   id, brand, category_id (nullable FK), name, match_key (unique per brand),
 *   points_value (nullable int >= 0), active, created_at, updated_at
 *
 * Backend model reference (ProductCategory):
 *   id, brand, name (unique per brand), description (nullable), active, created_at, updated_at
 */
import React, { useState } from "react";
import {
    Box, Button, Tabs, Tab, Divider,
} from "@mui/material";
import CategoryIcon from "@mui/icons-material/Category";
import InventoryIcon from "@mui/icons-material/Inventory";
import AddIcon from "@mui/icons-material/Add";

import { SECTION_COLORS } from "../../constants";
import { useLoyaltyApi } from "../../hooks/useLoyaltyApi";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { LOYALTY_API_BASE } from "../../constants";
import { useBrand } from "../../../../contexts/BrandContext";

// ── Product Categories sub-section ─────────────────────────────
import ProductCategoriesSection from "./productCategories/ProductCategoriesSection";
import CreateProductCategoryDialog from "./productCategories/CreateProductCategoryDialog";
import EditProductCategoryDialog from "./productCategories/EditProductCategoryDialog";
import ViewProductCategoryDialog from "./productCategories/ViewProductCategoryDialog";
import DeleteProductCategoryDialog from "./productCategories/DeleteProductCategoryDialog";

// ── Products sub-section ────────────────────────────────────────
import ProductsListSection from "./ProductsListSection";
import CreateProductDialog from "./CreateProductDialog";
import EditProductDialog from "./EditProductDialog";
import ViewProductDialog from "./ViewProductDialog";
import CatalogDeleteDialog, { formatCatalogDeleteStats } from "../shared/CatalogDeleteDialog";

const SUB_TABS = [
    { label: "Catégories", icon: <CategoryIcon sx={{ fontSize: 15 }} /> },
    { label: "Produits", icon: <InventoryIcon sx={{ fontSize: 15 }} /> },
];

const ProductsTab = ({ notify }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || "";
    const brandQuery = brandName ? `?brand=${encodeURIComponent(brandName)}` : "";

    // ── API state ─────────────────────────────────────────────
    const productCategoriesApi = useLoyaltyApi(`/admin/product-categories${brandQuery}`, !!brandName);
    const productsApi = useLoyaltyApi(`/admin/products${brandQuery}`, !!brandName);

    const [subTab, setSubTab] = useState(0);

    // ── ProductCategory local dialog state ──────────────────
    const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
    const [viewCategory, setViewCategory] = useState(null);
    const [editCategory, setEditCategory] = useState(null);
    const [deleteCategory, setDeleteCategory] = useState(null);
    const [deletingCategory, setDeletingCategory] = useState(false);

    // ── Product local dialog state ──────────────────────────
    const [createProductOpen, setCreateProductOpen] = useState(false);
    const [viewProduct, setViewProduct] = useState(null);
    const [editProduct, setEditProduct] = useState(null);
    const [deleteProduct, setDeleteProduct] = useState(null);

    // ── Pagination per sub-tab ───────────────────────────────
    const [pages, setPages] = useState([0, 0]);
    const handlePageChange = (idx) => (_, newPage) =>
        setPages((prev) => { const n = [...prev]; n[idx] = newPage; return n; });

    // ── Delete handlers ──────────────────────────────────────
    const handleDeleteCategoryConfirmed = async () => {
        if (!deleteCategory) return;
        setDeletingCategory(true);
        try {
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/product-categories/${deleteCategory.id}`, {
                method: "DELETE",
                headers: { "X-Brand": brandName },
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            setDeleteCategory(null);
            productCategoriesApi.refetch();
            notify(`Catégorie « ${deleteCategory.name} » supprimée avec succès`);
        } catch (err) {
            notify(err.message, "error");
        } finally {
            setDeletingCategory(false);
        }
    };

    // ── Add button per sub-tab ───────────────────────────────
    const addActions = [
        { label: "Ajouter une Catégorie", action: () => setCreateCategoryOpen(true) },
        { label: "Ajouter un Produit", action: () => setCreateProductOpen(true) },
    ];

    const productCategoriesList = Array.isArray(productCategoriesApi?.data) ? productCategoriesApi.data : [];

    return (
        <>
            {/* ── ProductCategory Dialogs ─────────────────────────── */}
            <CreateProductCategoryDialog
                open={createCategoryOpen}
                onClose={() => setCreateCategoryOpen(false)}
                onCreated={() => { productCategoriesApi.refetch(); notify("Catégorie créée avec succès"); }}
            />
            <ViewProductCategoryDialog
                open={Boolean(viewCategory)} category={viewCategory}
                onClose={() => setViewCategory(null)} onEditRequest={setEditCategory}
            />
            <EditProductCategoryDialog
                open={Boolean(editCategory)} category={editCategory}
                onClose={() => setEditCategory(null)}
                onSaved={() => { productCategoriesApi.refetch(); notify("Catégorie mise à jour avec succès"); }}
            />
            <DeleteProductCategoryDialog
                open={Boolean(deleteCategory)} category={deleteCategory}
                onClose={() => !deletingCategory && setDeleteCategory(null)}
                onConfirm={handleDeleteCategoryConfirmed}
                deleting={deletingCategory}
            />

            {/* ── Product Dialogs ─────────────────────────────────── */}
            <CreateProductDialog
                open={createProductOpen}
                onClose={() => setCreateProductOpen(false)}
                onCreated={() => { productsApi.refetch(); notify("Produit créé avec succès"); }}
                productCategories={productCategoriesList}
            />
            <ViewProductDialog
                open={Boolean(viewProduct)} product={viewProduct}
                onClose={() => setViewProduct(null)} onEditRequest={setEditProduct}
                productCategories={productCategoriesList}
            />
            <EditProductDialog
                open={Boolean(editProduct)} product={editProduct}
                onClose={() => setEditProduct(null)}
                onSaved={() => { productsApi.refetch(); notify("Produit mis à jour avec succès"); }}
                productCategories={productCategoriesList}
            />
            <CatalogDeleteDialog
                open={Boolean(deleteProduct)}
                resourceType="product"
                resource={deleteProduct ? { id: deleteProduct.id, name: deleteProduct.name } : null}
                brandName={brandName}
                onClose={() => setDeleteProduct(null)}
                onDeactivated={() => {
                    productsApi.refetch();
                    notify(`Produit « ${deleteProduct?.name} » désactivé`);
                }}
                onDeleted={(data) => {
                    productsApi.refetch();
                    const stats = formatCatalogDeleteStats(data);
                    notify(stats
                        ? `Produit supprimé — ${stats}`
                        : `Produit « ${deleteProduct?.name} » supprimé`);
                }}
            />

            {/* ── Sub-nav + "Add" button ──────────────────────────── */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                <Tabs
                    value={subTab}
                    onChange={(_, v) => setSubTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 36,
                        "& .MuiTabs-flexContainer": { gap: 0.5 },
                        "& .MuiTab-root": {
                            textTransform: "none",
                            fontWeight: 600,
                            fontFamily: "Montserrat, sans-serif",
                            fontSize: 12,
                            minHeight: 36,
                            borderRadius: 2,
                            px: 1.5,
                            color: "#64748b",
                            transition: "all 0.2s",
                            "&:hover": { bgcolor: `${SECTION_COLORS.rewards.main}10`, color: SECTION_COLORS.rewards.main },
                        },
                        "& .Mui-selected": {
                            color: `${SECTION_COLORS.rewards.main} !important`,
                            bgcolor: `${SECTION_COLORS.rewards.main}12`,
                        },
                        "& .MuiTabs-indicator": {
                            height: 2,
                            borderRadius: "2px 2px 0 0",
                            background: SECTION_COLORS.rewards.gradient,
                        },
                    }}
                >
                    {SUB_TABS.map((t, idx) => (
                        <Tab
                            key={idx}
                            label={
                                <Box display="flex" alignItems="center" gap={0.6}>
                                    {t.icon}
                                    {t.label}
                                </Box>
                            }
                        />
                    ))}
                </Tabs>

                <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={addActions[subTab].action}
                    disabled={!brandName}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: 12,
                        borderRadius: 2,
                        background: SECTION_COLORS.rewards.gradient,
                        boxShadow: `0 2px 8px ${SECTION_COLORS.rewards.main}30`,
                        "&:hover": { boxShadow: `0 4px 14px ${SECTION_COLORS.rewards.main}40` },
                    }}
                >
                    {addActions[subTab].label}
                </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* ── Sub-section content ──────────────────────────────── */}
            {subTab === 0 && (
                <ProductCategoriesSection
                    productCategoriesApi={productCategoriesApi}
                    page={pages[0]}
                    onPageChange={handlePageChange(0)}
                    onView={setViewCategory}
                    onEdit={setEditCategory}
                    onDelete={setDeleteCategory}
                />
            )}
            {subTab === 1 && (
                <ProductsListSection
                    productsApi={productsApi}
                    productCategories={productCategoriesList}
                    page={pages[1]}
                    onPageChange={handlePageChange(1)}
                    onView={setViewProduct}
                    onEdit={setEditProduct}
                    onDelete={setDeleteProduct}
                />
            )}
        </>
    );
};

export default ProductsTab;
